import { bookSections, bookTitle } from "@/mocks/bookData";

interface BookTOCProps {
  activeSection: string;
  onNavigate: (id: string) => void;
}

const castEntry = {
  id: "characters",
  number: "—",
  title: "Cast of Characters",
  subChapters: [] as string[],
};

export default function BookTOC({ activeSection, onNavigate }: BookTOCProps) {
  const allEntries = [castEntry, ...bookSections];

  return (
    <section id="toc" className="w-full px-6 py-24 bg-[#0A0A0A] border-t border-[#111]">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-[0.45em] uppercase mb-4">
            Navigation
          </p>
          <h2 className="font-['Special_Elite'] text-white text-5xl md:text-6xl mb-3">
            {bookTitle}
          </h2>
          <div className="font-['Special_Elite'] text-[#555] text-xl italic">
            Table of Contents
          </div>
          <div className="w-24 h-px bg-[#C8922A]/30 mx-auto mt-6" />
        </div>

        {/* TOC Entries */}
        <div className="flex flex-col">
          {allEntries.map((entry) => {
            const isActive = activeSection === entry.id;
            return (
              <button
                key={entry.id}
                onClick={() => onNavigate(entry.id)}
                className={`group flex flex-col py-5 border-b transition-all duration-300 cursor-pointer text-left ${
                  isActive ? "border-[#C8922A]/30" : "border-[#1A1A1A] hover:border-[#2A2A2A]"
                }`}
              >
                <div className="flex items-end gap-4 w-full">
                  {/* Roman Numeral */}
                  <span
                    className={`font-['Share_Tech_Mono'] text-xs tracking-[0.3em] uppercase w-10 flex-shrink-0 pb-0.5 transition-colors duration-300 ${
                      isActive ? "text-[#C8922A]" : "text-[#3A3A3A] group-hover:text-[#555]"
                    }`}
                  >
                    {entry.number}
                  </span>

                  {/* Chapter Title */}
                  <span
                    className={`font-['Special_Elite'] text-lg md:text-xl flex-shrink-0 transition-colors duration-300 ${
                      isActive ? "text-white" : "text-[#777] group-hover:text-[#AAA]"
                    }`}
                  >
                    {entry.title}
                  </span>

                  {/* Dotted Line */}
                  <span className="flex-1 border-b border-dotted border-[#222] mb-1.5 group-hover:border-[#2C2C2C] transition-colors duration-300" />

                  {/* Sub-chapters */}
                  {entry.subChapters.length > 0 && (
                    <span
                      className={`font-['Share_Tech_Mono'] text-xs tracking-[0.15em] flex-shrink-0 pb-0.5 transition-colors duration-300 ${
                        isActive ? "text-[#C8922A]/70" : "text-[#333] group-hover:text-[#444]"
                      }`}
                    >
                      {entry.subChapters.join(" · ")}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Decorative bottom */}
        <div className="flex items-center justify-center gap-4 mt-12">
          <div className="w-16 h-px bg-[#1E1E1E]" />
          <i className="ri-sword-line text-[#C8922A]/30 text-sm" />
          <div className="w-16 h-px bg-[#1E1E1E]" />
        </div>
      </div>
    </section>
  );
}
